import { message, Modal } from 'antd';
import React from 'react';

import { useBatchSetDomainMutation } from '../../../../graphql/mutations.generated';
import { DomainAssociation, SubResourceType } from '../../../../types.generated';
import { useTranslation } from '../../../../i18n/useTranslation';
import { DomainLink } from '../DomainLink';

interface Props {
    domain: DomainAssociation;
    entityUrn?: string;
    entitySubresource?: string;
    canRemove?: boolean;
    readOnly?: boolean;
    onOpenModal?: () => void;
    refetch?: () => Promise<any>;
    fontSize?: number;
}

export default function Domain({
    domain,
    entityUrn,
    entitySubresource,
    canRemove,
    readOnly,
    onOpenModal,
    refetch,
    fontSize,
}: Props) {
    const { t } = useTranslation();
    const [removeDomainMutation] = useBatchSetDomainMutation();
    const removeDomain = (domainAssociationToRemove: DomainAssociation) => {
        const domainToRemove = domainAssociationToRemove.domain;
        onOpenModal?.();
        Modal.confirm({
            title: t('Do you want to remove {{domainName}} domain?', { domainName: domainToRemove?.properties?.name }),
            content: t('Are you sure you want to remove the {{domainName}} domain?', {
                domainName: domainToRemove?.properties?.name,
            }),
            onOk() {
                if (domainAssociationToRemove.associatedUrn || entityUrn) {
                    removeDomainMutation({
                        variables: {
                            input: {
                                resources: [
                                    {
                                        resourceUrn: domainAssociationToRemove.associatedUrn || entityUrn || '',
                                        subResource: entitySubresource,
                                        subResourceType: entitySubresource ? SubResourceType.DatasetField : null,
                                    },
                                ],
                            },
                        },
                    })
                        .then(({ errors }) => {
                            if (!errors) {
                                message.success({ content: t('Removed Domain!'), duration: 2 });
                            }
                        })
                        .then(refetch)
                        .catch((e) => {
                            message.destroy();
                            message.error({
                                content: t('Failed to remove domain: {{message}}', { message: e.message || '' }),
                                duration: 3,
                            });
                        });
                }
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    return (
        <DomainLink
            closable={canRemove && !readOnly}
            readOnly={readOnly}
            onClose={(e) => {
                e.preventDefault();
                removeDomain(domain);
            }}
            fontSize={fontSize}
            domain={domain.domain}
        />
    );
}
